import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  ContentCopy,
  Delete as DeleteIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { transportationJobService } from '../services/transportationJobService';
import { orderService } from '../services/orderService';
import { employeeService } from '../services/employeeService';
import { vendorService } from '../services/vendorService';
import CurrencyTextField, { formatCurrency } from '../components/CurrencyTextField';
import MilestoneUploadCard from '../components/transport/MilestoneUploadCard';
import { useCompany } from '../context/CompanyProvider';
import { useAuth } from '../hooks/useAuth';
import { formatDateOnly, parseApiTimestamp } from '../utils/dateUtils';
import {
  TRANSPORT_JOB_TYPES,
  TRANSPORT_STATUS_ORDER,
  driverDisplayName,
  formatAddressLines,
  formatDateInput,
  formatTimeInput,
  transportStatusMeta,
} from '../constants/transportation';

const emptyForm = (company) => ({
  work_order_id: null,
  driver_employee_id: null,
  vendor_id: null,
  driver_name: '',
  job_type: 'delivery',
  pickup_name: company?.legalName || company?.label || 'FabWorks',
  pickup_address: (company?.print?.addressLines || []).join(', '),
  pickup_date: '',
  pickup_time: '',
  dropoff_name: '',
  dropoff_address: '',
  dropoff_date: '',
  dropoff_time: '',
  rate: 0,
  fuel_fees: 0,
  billable_to_customer: true,
  rate_visible_to_driver: false,
  notes: '',
});

function jobToForm(job, company) {
  const base = emptyForm(company);
  if (!job) return base;
  return {
    work_order_id: job.work_order_id || null,
    driver_employee_id: job.driver_employee_id || null,
    vendor_id: job.vendor_id || null,
    driver_name: driverDisplayName(job) || '',
    job_type: job.job_type || 'delivery',
    pickup_name: job.pickup_name || base.pickup_name,
    pickup_address: job.pickup_address || base.pickup_address,
    pickup_date: formatDateInput(job.pickup_date),
    pickup_time: formatTimeInput(job.pickup_time),
    dropoff_name: job.dropoff_name || '',
    dropoff_address: job.dropoff_address || '',
    dropoff_date: formatDateInput(job.dropoff_date),
    dropoff_time: formatTimeInput(job.dropoff_time),
    rate: Number(job.rate || 0),
    fuel_fees: Number(job.fuel_fees || 0),
    billable_to_customer: job.billable_to_customer !== false,
    rate_visible_to_driver: job.rate_visible_to_driver === true,
    notes: job.notes || '',
  };
}

function formatStamp(value) {
  if (!value) return '';
  const d = parseApiTimestamp(value);
  if (!d) return '';
  return `${formatDateOnly(d.toISOString().slice(0, 10))} ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
}

const TransportationJobDetail = () => {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { company } = useCompany();
  const { isAdmin } = useAuth();
  const admin = typeof isAdmin === 'function' && isAdmin();
  const [job, setJob] = useState(null);
  const [form, setForm] = useState(() => emptyForm(company));
  const [savedForm, setSavedForm] = useState(() => emptyForm(company));
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [orders, setOrders] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [uploadingKind, setUploadingKind] = useState('');
  const [acting, setActing] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);

  const dirty = JSON.stringify(form) !== JSON.stringify(savedForm);
  const readOnly = job && ['complete', 'cancelled'].includes(job.status);
  const files = job?.files || [];

  const loadLookups = useCallback(async () => {
    try {
      const [orderRes, empRes, vendorRes] = await Promise.all([
        orderService.getAll({ limit: 100 }),
        employeeService.getAll({ limit: 500, activeOnly: 'true' }),
        vendorService.getAll({ limit: 200, activeOnly: true }),
      ]);
      setOrders(orderRes.data || []);
      const empList = empRes.data || empRes || [];
      setEmployees(Array.isArray(empList) ? empList : []);
      setVendors(vendorRes.data || []);
    } catch {
      /* lookups optional */
    }
  }, []);

  const applyJob = useCallback((row) => {
    setJob(row);
    const next = jobToForm(row, company);
    setForm(next);
    setSavedForm(next);
  }, [company]);

  const loadJob = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    setError('');
    try {
      const res = await transportationJobService.getById(id);
      applyJob(res.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load job');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, applyJob]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    if (isNew) {
      const next = emptyForm(company);
      setForm(next);
      setSavedForm(next);
      setJob(null);
    } else {
      loadJob();
    }
  }, [isNew, company, loadJob]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const orderOptions = useMemo(() => {
    const list = [...orders];
    if (job?.work_order_id && !list.some((o) => o.id === job.work_order_id)) {
      list.unshift({
        id: job.work_order_id,
        identifier: job.work_order_identifier,
        work_order_number: job.work_order_number,
      });
    }
    return list;
  }, [orders, job]);

  const selectedOrder = useMemo(
    () => orderOptions.find((o) => o.id === form.work_order_id) || null,
    [orderOptions, form.work_order_id]
  );

  const driverOptions = useMemo(() => {
    const empOpts = employees.map((e) => ({
      key: `e-${e.id}`,
      kind: 'employee',
      id: e.id,
      label: `${[e.first_name, e.last_name].filter(Boolean).join(' ').trim() || `#${e.id}`}`,
    }));
    const vendorOpts = vendors.map((v) => ({
      key: `v-${v.id}`,
      kind: 'vendor',
      id: v.id,
      label: v.name,
    }));
    return [...empOpts, ...vendorOpts];
  }, [employees, vendors]);

  const selectedDriver = useMemo(() => {
    if (form.driver_employee_id) {
      return driverOptions.find((o) => o.kind === 'employee' && o.id === form.driver_employee_id) || null;
    }
    if (form.vendor_id) {
      return driverOptions.find((o) => o.kind === 'vendor' && o.id === form.vendor_id) || null;
    }
    return null;
  }, [driverOptions, form.driver_employee_id, form.vendor_id]);

  const handleOrderChange = async (order) => {
    if (!order) {
      setForm((prev) => ({ ...prev, work_order_id: null }));
      return;
    }
    setForm((prev) => ({ ...prev, work_order_id: order.id }));
    try {
      const res = await orderService.getById(order.id);
      const wo = res.data || res;
      const dropoffName = wo.company_name || '';
      const dropoffAddress = formatAddressLines([
        wo.shipping_address_line1,
        wo.shipping_address_line2,
        wo.shipping_city,
        wo.shipping_state,
        wo.shipping_zip,
      ]);
      setForm((prev) => ({
        ...prev,
        work_order_id: order.id,
        dropoff_name: prev.dropoff_name || dropoffName,
        dropoff_address: prev.dropoff_address || dropoffAddress,
      }));
      if (!orders.some((o) => o.id === order.id)) {
        setOrders((prev) => [order, ...prev]);
      }
    } catch {
      /* keep selected id */
    }
  };

  const persist = async () => {
    const payload = {
      ...form,
      work_order_id: form.work_order_id || null,
      driver_employee_id: form.driver_employee_id || null,
      vendor_id: form.vendor_id || null,
      rate: Number(form.rate || 0),
      fuel_fees: Number(form.fuel_fees || 0),
      rate_visible_to_driver: form.rate_visible_to_driver === true,
    };
    if (isNew) {
      const res = await transportationJobService.create(payload);
      return res.data;
    }
    const res = await transportationJobService.update(job.id, payload);
    return res.data;
  };

  const handleSave = async ({ close } = {}) => {
    setSaving(true);
    setError('');
    try {
      const saved = await persist();
      applyJob(saved);
      if (close) navigate('/transportation');
      else if (isNew) navigate(`/transportation/${saved.id}`, { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save job');
    } finally {
      setSaving(false);
    }
  };

  const ensureSaved = async () => {
    if (isNew || dirty) {
      const saved = await persist();
      applyJob(saved);
      if (isNew) navigate(`/transportation/${saved.id}`, { replace: true });
      return saved;
    }
    return job;
  };

  const handleUpload = async (kind, file) => {
    setUploadingKind(kind);
    try {
      const current = await ensureSaved();
      await transportationJobService.uploadFile(current.id, { file, kind });
      const res = await transportationJobService.getById(current.id);
      applyJob(res.data);
    } finally {
      setUploadingKind('');
    }
  };

  const handleDeleteFile = async (file) => {
    if (!job) return;
    await transportationJobService.deleteFile(job.id, file.id);
    const res = await transportationJobService.getById(job.id);
    applyJob(res.data);
  };

  const runAction = async (key, fn) => {
    if (!job) return;
    setActing(key);
    setError('');
    try {
      const res = await fn(job.id);
      applyJob(res.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Action failed');
    } finally {
      setActing('');
    }
  };

  const copyPortalLink = async () => {
    if (!job?.portal_path) return;
    const url = `${window.location.origin}${job.portal_path}`;
    try {
      await navigator.clipboard.writeText(url);
      setToast('Driver link copied');
    } catch {
      setToast(url);
    }
  };

  const canDelete = admin && job && job.status !== 'complete' && !isNew;

  const handleDelete = async () => {
    if (!job || !canDelete) return;
    setActing('delete');
    setError('');
    try {
      await transportationJobService.delete(job.id);
      navigate('/transportation');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete job');
      setDeleteOpen(false);
    } finally {
      setActing('');
    }
  };

  const total = Number(form.rate || 0) + Number(form.fuel_fees || 0);
  const titleId = job?.identifier || (isNew ? 'New' : `#${id}`);
  const statusMeta = transportStatusMeta(job?.status || 'assigned');
  const filesByKind = (kind) => files.filter((f) => f.kind === kind);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/transportation')} sx={{ textTransform: 'none' }}>
          Jobs
        </Button>
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Transportation Job / {titleId}
            </Typography>
            <Chip size="small" label={statusMeta.label} color={statusMeta.color} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {canDelete && (
            <Button
              color="error"
              startIcon={<DeleteIcon />}
              disabled={!!acting}
              onClick={() => setDeleteOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              Delete
            </Button>
          )}
          <Button onClick={() => navigate('/transportation')} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="outlined" disabled={saving || readOnly || (!dirty && !isNew)} onClick={() => handleSave()}>
            Save
          </Button>
          <Button variant="contained" disabled={saving || (readOnly && !isNew)} onClick={() => handleSave({ close: true })}>
            Save & Close
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 2 }}>
            <CardHeader title="Job Details" titleTypographyProps={{ variant: 'h6' }} />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    options={orderOptions}
                    value={selectedOrder}
                    disabled={readOnly}
                    getOptionLabel={(o) => o.identifier || o.work_order_number || ''}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    onChange={(_e, v) => handleOrderChange(v)}
                    renderInput={(params) => <TextField {...params} label="Reference" size="small" />}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    options={driverOptions}
                    value={selectedDriver}
                    disabled={readOnly}
                    groupBy={(o) => (o.kind === 'vendor' ? 'Vendors' : 'Employees')}
                    getOptionLabel={(o) => o.label || ''}
                    isOptionEqualToValue={(a, b) => a.key === b.key}
                    onChange={(_e, v) => {
                      if (!v) {
                        setForm((prev) => ({
                          ...prev,
                          driver_employee_id: null,
                          vendor_id: null,
                          driver_name: '',
                        }));
                        return;
                      }
                      setForm((prev) => ({
                        ...prev,
                        driver_employee_id: v.kind === 'employee' ? v.id : null,
                        vendor_id: v.kind === 'vendor' ? v.id : null,
                        driver_name: v.label,
                      }));
                    }}
                    renderInput={(params) => <TextField {...params} label="Driver / Vendor" size="small" />}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Job Type"
                    value={form.job_type}
                    onChange={(e) => setField('job_type', e.target.value)}
                    disabled={readOnly}
                  >
                    {TRANSPORT_JOB_TYPES.map((t) => (
                      <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Pickup" titleTypographyProps={{ variant: 'h6' }} />
                <Divider />
                <CardContent>
                  <TextField
                    fullWidth
                    size="small"
                    label="From"
                    value={form.pickup_name}
                    onChange={(e) => setField('pickup_name', e.target.value)}
                    disabled={readOnly}
                    sx={{ mb: 1.5 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Address"
                    value={form.pickup_address}
                    onChange={(e) => setField('pickup_address', e.target.value)}
                    disabled={readOnly}
                    sx={{ mb: 1.5 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="Date"
                      InputLabelProps={{ shrink: true }}
                      value={form.pickup_date}
                      onChange={(e) => setField('pickup_date', e.target.value)}
                      disabled={readOnly}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      type="time"
                      label="Time"
                      InputLabelProps={{ shrink: true }}
                      value={form.pickup_time}
                      onChange={(e) => setField('pickup_time', e.target.value)}
                      disabled={readOnly}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Drop Off" titleTypographyProps={{ variant: 'h6' }} />
                <Divider />
                <CardContent>
                  <TextField
                    fullWidth
                    size="small"
                    label="To"
                    value={form.dropoff_name}
                    onChange={(e) => setField('dropoff_name', e.target.value)}
                    disabled={readOnly}
                    sx={{ mb: 1.5 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Address"
                    value={form.dropoff_address}
                    onChange={(e) => setField('dropoff_address', e.target.value)}
                    disabled={readOnly}
                    sx={{ mb: 1.5 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="Date"
                      InputLabelProps={{ shrink: true }}
                      value={form.dropoff_date}
                      onChange={(e) => setField('dropoff_date', e.target.value)}
                      disabled={readOnly}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      type="time"
                      label="Time"
                      InputLabelProps={{ shrink: true }}
                      value={form.dropoff_time}
                      onChange={(e) => setField('dropoff_time', e.target.value)}
                      disabled={readOnly}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card sx={{ mb: 2 }}>
            <CardHeader title="Rate / Cost" titleTypographyProps={{ variant: 'h6' }} />
            <Divider />
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <CurrencyTextField
                    fullWidth
                    size="small"
                    label="Rate"
                    value={form.rate}
                    onChange={(e) => setField('rate', e.target.value)}
                    disabled={readOnly}
                    dollarAdornment
                    minimumFractionDigits={2}
                    maximumFractionDigits={2}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <CurrencyTextField
                    fullWidth
                    size="small"
                    label="Fuel / Fees"
                    value={form.fuel_fees}
                    onChange={(e) => setField('fuel_fees', e.target.value)}
                    disabled={readOnly}
                    dollarAdornment
                    minimumFractionDigits={2}
                    maximumFractionDigits={2}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Total"
                    value={formatCurrency(total)}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.billable_to_customer}
                        onChange={(e) => setField('billable_to_customer', e.target.checked)}
                        disabled={readOnly}
                      />
                    }
                    label="Billable to Customer"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.rate_visible_to_driver}
                        onChange={(e) => setField('rate_visible_to_driver', e.target.checked)}
                        disabled={readOnly}
                      />
                    }
                    label="Show pay amount to driver"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ mb: 2 }}>
            <CardHeader title="Notes / Instructions" titleTypographyProps={{ variant: 'h6' }} />
            <Divider />
            <CardContent>
              <TextField
                fullWidth
                multiline
                minRows={3}
                value={form.notes}
                onChange={(e) => setField('notes', e.target.value)}
                disabled={readOnly}
                placeholder="Deliver fabricated platforms and components to customer."
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardHeader title="Job Status" titleTypographyProps={{ variant: 'h6' }} />
            <Divider />
            <CardContent>
              {TRANSPORT_STATUS_ORDER.map((key, idx) => {
                const meta = transportStatusMeta(key);
                const reached = TRANSPORT_STATUS_ORDER.indexOf(job?.status || 'assigned') >= idx
                  && job?.status !== 'cancelled';
                const current = (job?.status || 'assigned') === key;
                const stamps = {
                  assigned: job?.created_at,
                  picked_up: job?.picked_up_at,
                  delivered: job?.delivered_at,
                  pending_approval: job?.submitted_at,
                  complete: job?.completed_at,
                };
                return (
                  <Box key={key} sx={{ display: 'flex', gap: 1.5, minHeight: 52 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 18 }}>
                      <Box
                        sx={{
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          bgcolor: current ? 'primary.main' : reached ? 'primary.light' : 'grey.300',
                          border: current ? '2px solid' : '0',
                          borderColor: 'primary.dark',
                        }}
                      />
                      {idx < TRANSPORT_STATUS_ORDER.length - 1 && (
                        <Box sx={{ width: 2, flexGrow: 1, bgcolor: reached ? 'primary.light' : 'grey.200', my: 0.5 }} />
                      )}
                    </Box>
                    <Box sx={{ pb: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: current ? 700 : 500 }}>
                        {meta.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatStamp(stamps[key]) || (current ? 'Current' : '')}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
              {job?.status === 'cancelled' && (
                <Chip size="small" label="Cancelled" sx={{ mt: 1 }} />
              )}
            </CardContent>
          </Card>

          {job && (
            <Card sx={{ mb: 2 }}>
              <CardHeader title="Driver portal" titleTypographyProps={{ variant: 'h6' }} />
              <Divider />
              <CardContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Send this link to the driver. They can upload photos and docs from their phone without an employee login.
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={job.portal_path ? `${window.location.origin}${job.portal_path}` : ''}
                  InputProps={{ readOnly: true }}
                  sx={{ mb: 1 }}
                />
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button size="small" startIcon={<ContentCopy />} onClick={copyPortalLink} sx={{ textTransform: 'none' }}>
                    Copy link
                  </Button>
                  <Button
                    size="small"
                    startIcon={<LinkIcon />}
                    href={job.portal_path}
                    target="_blank"
                    rel="noreferrer"
                    sx={{ textTransform: 'none' }}
                  >
                    Open
                  </Button>
                  {!readOnly && (
                    <Button
                      size="small"
                      disabled={acting === 'regen'}
                      onClick={() => runAction('regen', transportationJobService.regeneratePortalToken)}
                      sx={{ textTransform: 'none' }}
                    >
                      New link
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}

          {job && !readOnly && (
            <Card>
              <CardHeader title="Office actions" titleTypographyProps={{ variant: 'h6' }} />
              <Divider />
              <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {job.status === 'assigned' && (
                  <Button
                    variant="outlined"
                    disabled={!!acting}
                    onClick={() => runAction('pickup', transportationJobService.markPickedUp)}
                  >
                    Mark Picked Up
                  </Button>
                )}
                {['assigned', 'picked_up'].includes(job.status) && (
                  <Button
                    variant="outlined"
                    disabled={!!acting}
                    onClick={() => runAction('delivered', transportationJobService.markDelivered)}
                  >
                    Mark Delivered
                  </Button>
                )}
                {['assigned', 'picked_up', 'delivered'].includes(job.status) && (
                  <Button
                    variant="outlined"
                    disabled={!!acting}
                    onClick={() => runAction('submit', transportationJobService.submitForApproval)}
                  >
                    Submit for Approval
                  </Button>
                )}
                {['pending_approval', 'delivered'].includes(job.status) && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircle />}
                    disabled={!!acting}
                    onClick={() => runAction('approve', transportationJobService.approve)}
                  >
                    Approve & Complete
                  </Button>
                )}
                {job.status !== 'cancelled' && (
                  <Button
                    color="error"
                    disabled={!!acting}
                    onClick={() => runAction('cancel', transportationJobService.cancel)}
                  >
                    Cancel job
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <MilestoneUploadCard
                title="Proof of Pickup"
                hint="Upload Photo (Taken at pickup)"
                emptyLabel="No photo uploaded yet"
                files={filesByKind('pickup')}
                disabled={readOnly}
                uploading={uploadingKind === 'pickup'}
                onUpload={(file) => handleUpload('pickup', file)}
                onDelete={handleDeleteFile}
                onOpenFile={job ? (file) => transportationJobService.openFile(job.id, file.id) : undefined}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MilestoneUploadCard
                title="Proof of Delivery"
                hint="Upload Photo (Taken at delivery)"
                emptyLabel="No photo uploaded yet"
                files={filesByKind('delivery')}
                disabled={readOnly}
                uploading={uploadingKind === 'delivery'}
                onUpload={(file) => handleUpload('delivery', file)}
                onDelete={handleDeleteFile}
                onOpenFile={job ? (file) => transportationJobService.openFile(job.id, file.id) : undefined}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MilestoneUploadCard
                title="Documents"
                hint="Upload Signed Packing Slip or other documents"
                emptyLabel="No documents uploaded"
                accept="image/*,.pdf,application/pdf"
                icon="document"
                files={filesByKind('document')}
                disabled={readOnly}
                uploading={uploadingKind === 'document'}
                onUpload={(file) => handleUpload('document', file)}
                onDelete={handleDeleteFile}
                onOpenFile={job ? (file) => transportationJobService.openFile(job.id, file.id) : undefined}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3000}
        onClose={() => setToast('')}
        message={toast}
      />
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete transportation job?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete {job?.identifier || 'this job'}? Photos and documents on the job will be removed. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Keep job</Button>
          <Button color="error" variant="contained" disabled={acting === 'delete'} onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TransportationJobDetail;
